/**
 *  @function SectionHandoff
 *
 *  The "handoff flight" of the product image across the section boundaries on both sides of Product showcase (Part 1):
 *   A: the product in the last frame of scroll-video-split's left video → Part 1's opening large image (small to large;
 *      at takeoff the left video fades out entirely — the product is burned into the footage, so two products would give it away);
 *   B: the small product parked after Part 1's pin ends → capture-scatter's centered main image
 *      (late takeoff: it only breaks away into the dive once the landing spot rises to near the bottom edge of the viewport).
 *
 *  Mechanism: during both handoff windows, the document coordinates of the anchors at both ends are static — Part 1's
 *  sticky follows document flow before the pin starts, and parks at the bottom of its container after the pin ends. The ghost <img>
 *  is absolutely positioned on body and tweens from the source rect to the target rect in document coordinates, one
 *  ScrollTrigger per segment (scrub: true binds it rigidly to scrolling: zero lag at both ends of the handoff, and reverse playback
 *  works naturally), with zero per-frame layout reads. The show/hide handoff between the real image and the ghost is managed by applyStates(),
 *  an idempotent state machine whose only input is scrollY.
 *
 *  Device gate: fine pointer + hover + ≥1100px (= the lower bound of the desktop layout of the sections at both ends), not
 *  reduced-motion, not low-end; if not eligible nothing is initialized and the three sections stay
 *  as they are. If the adjacent sections don't qualify (missing / other sections in between / main image not configured), the corresponding
 *  handoff segment is silently disabled on its own. Add ?handoff-debug to the URL to outline the source/target rects.
 */

/* Takeoff scroll position for handoff A. The park point (parkScrollY) published by the upstream scroll-video-split is the
      single source of truth, and takeoff **equals** it — no deviation allowed in either direction:

          earlier than park → the footage is still moving; the ghost flies off in the last-frame pose while the strip still shows the tilted pose of the previous stop,
                                two products visible at once and not matching (2026-07-17 images #16/#17).
          later than park → the strip fades out within 0.04 of travel after parking (HANDOFF_FADE in scroll-video-split.js),
                                and over the scroll distance between the fade finishing and takeoff neither is visible, showing up as "scrolled here and the product
                                didn't appear" (2026-07-25 feedback). The old implementation used max(leadStart, park), which only blocked
                                the first half.

      handoff_lead is therefore only a fallback when upstream has no park point (geometry mode, or the strip hasn't measured its intrinsic size yet).
      It's written as a module-level pure function so tests/unit/svs-handoff-takeoff.test.mjs can require it directly. */
function takeoffScrollY({ leadStart, park, endY }) {
	const start = Number.isFinite(park) ? park : leadStart;
	// Leave at least a 1px window, otherwise ScrollTrigger can't build this flight path
	return Math.min(start, endY - 1);
}

(() => {
	// In node this file is required only to get the pure function above (see the export at the end of the file); with no DOM
	// don't go any further — the very next line reads location.search.
	if (typeof window === "undefined" || typeof document === "undefined") {
		return;
	}
	// Early-exit diagnostics: this file has several gates, and failing any one of them means "the whole handoff isn't initialized",
	// which looks exactly the same as "the animation is broken". With the debug parameter, log whichever gate failed,
	// so nobody again tunes geometry on a module that never even started (2026-07-20).
	const HO_DEBUG = /handoff-debug|svs-debug/.test(location.search);
	// On-screen probe: scroll-video-split's [v2] floating box reads __handoffPeek every frame and draws it
	// — users tend to send screenshots rather than dig through the Console, so one screenshot carries the whole handoff state.
	// First set up a "GSAP gate not passed" fallback; once past the gate it's overwritten with the real probe at the end of the file.
	window.__handoffPeek = () => "GSAP/ScrollTrigger not loaded";
	if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
		if (HO_DEBUG) {
			console.warn("[handoff] not initialized: GSAP/ScrollTrigger not loaded", {
				gsap: typeof gsap,
				ScrollTrigger: typeof ScrollTrigger,
			});
		}
		return;
	}

	const DEBUG = new URLSearchParams(location.search).has("handoff-debug");
	const num = (v, dflt) => {
		const n = parseFloat(v);
		return Number.isFinite(n) ? n : dflt;
	};

	// ≥1100 rather than the original 900: both ends of the handoff (scroll-video-split's parked product,
	// Product showcase (Part 1)'s sticky main image) have already switched to their mobile layouts below 1100 —
	// the former has no left-column strip, the latter is entirely display:none. If the gate stayed at 900, between 900~1100 it would
	// compute rects for two nonexistent/invisible anchors and launch the ghost image (synced on 2026-07-24 when the desktop layout
	// lower bound of both sections was raised to 1100).
	const mqDesktop = window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 1100px)");
	const mqMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
	const isLowEnd = () =>
		(navigator.deviceMemory && navigator.deviceMemory <= 4) ||
		(navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ||
		(navigator.connection && navigator.connection.saveData) ||
		false;
	const eligible = () => mqDesktop.matches && !mqMotion.matches && !isLowEnd();
	if (HO_DEBUG) {
		console.log("[handoff] device gate", {
			passed: eligible(),
			"desktop(hover+fine+≥1100px)": mqDesktop.matches,
			viewportWidth: window.innerWidth,
			"reduced-motion": mqMotion.matches,
			lowEndDevice: isLowEnd(),
			deviceMemory: navigator.deviceMemory,
			cpuCores: navigator.hardwareConcurrency,
			saveData: navigator.connection && navigator.connection.saveData,
		});
	}

	// The ghost image reuses the real image's src/srcset/sizes so the browser picks a suitable resolution; sizes is
	// tightened to the landing width, to avoid fetching an extra large image for the ghost.
	// Last-frame image URL published by the strip (only handoff A has a video; handoff B doesn't and takes the original path)
	const svsHost2 = (video) => (video && video.closest ? video.closest("scroll-video-split") : null);

	const svsGhostSrc = (video) => {
		const host = video && video.closest && video.closest("scroll-video-split");
		return host && host.dataset.ghostSrc ? host.dataset.ghostSrc : null;
	};

	const copySources = (ghost, img, width, override) => {
		// Prefer the **footage last-frame** image published upstream (scroll-video-split's data-ghost-src).
		// When reusing Part 1's product photo, the pose/lighting isn't the same image as the footage's last frame, so however precise the geometry there's a seam
		// (user feedback "the original product image and the left video don't match", 2026-07-26). The last-frame image is cropped precisely to the product's
		// bounding box, matching the two footageProduct* constants by construction.
		if (override) {
			ghost.removeAttribute("srcset");
			ghost.removeAttribute("sizes");
			ghost.src = override;
			return;
		}
		const srcset = img.getAttribute("srcset");
		if (srcset) {
			ghost.setAttribute("srcset", srcset);
			ghost.setAttribute("sizes", Math.round(width) + "px");
		}
		ghost.src = img.currentSrc || img.src;
	};

	class Handoff {
		constructor(container) {
			this.container = container;
			this.sid = container.id.replace(/^soulog-/, "");
			this.product = document.getElementById("soulog-product-" + this.sid);
			this.sticky = document.getElementById("soulog-sticky-" + this.sid);
			// The indicator light must show/hide together with the real product (otherwise the product is still flying while the light is already lit at the landing spot).
			// Only write the inner dot's opacity: the outer #soulog-indicator-* belongs to the GSAP timeline
			// (x/y/autoAlpha), so the two sides never write the same element.
			this.indicatorDot = document.querySelector("#soulog-indicator-" + this.sid + " .soulog-indicator-dot");
			this.section = container.closest(".shopify-section");
			this.flights = [];
			this.debugEls = [];
			this.productShown = null;
			// When the main image isn't configured Liquid renders a placeholder div, so there's no anchor to speak of → disable entirely
			if (HO_DEBUG) {
				console.log("[handoff] element check", {
					mainImageId: "soulog-product-" + this.sid,
					foundMainImage: !!this.product,
					isIMG: this.product && this.product.tagName === "IMG",
					sticky: !!this.sticky,
					section: !!this.section,
				});
			}
			if (!this.product || this.product.tagName !== "IMG" || !this.sticky || !this.section) {
				return;
			}

			const d = container.dataset;
			if (d.handoffPrev === "true") {
				this.initA(d);
			}
			if (d.handoffNext === "true") {
				this.initB(d);
			}
			if (!this.flights.length) {
				return;
			}

			this.raf = null;
			this.onScroll = () => {
				if (this.raf) {
					return;
				}
				this.raf = requestAnimationFrame(() => {
					this.raf = null;
					this.applyStates();
				});
			};
			window.addEventListener("scroll", this.onScroll, { passive: true });
			this.applyStates();
		}

		// The sticky container (#soulog-sticky-*, not inner) is always in document flow,
		// so whether or not it's currently pinned, document coordinates can be safely converted.
		stickyRect() {
			const r = this.sticky.getBoundingClientRect();
			return { left: r.left, docTop: r.top + window.scrollY, height: r.height };
		}

		anchors() {
			const ds = this.product.dataset;
			return {
				startScale: num(ds.hoStartScale, 1),
				startX: num(ds.hoStartX, 0),
				startY: num(ds.hoStartY, 0),
				endScale: num(ds.hoEndScale, 1),
				endX: num(ds.hoEndX, 0),
				endY: num(ds.hoEndY, 0),
				pw: num(ds.hoPw, this.product.offsetWidth),
				ph: num(ds.hoPh, this.product.offsetHeight),
			};
		}

		/* ---------- Handoff A: video last frame → Part 1 opening large image ---------- */

		initA(d) {
			const prev = this.section.previousElementSibling;
			const svs = prev && prev.querySelector ? prev.querySelector("scroll-video-split") : null;
			const video = svs ? svs.querySelector(".scroll-video-split__scrub") : null;
			if (HO_DEBUG) {
				console.log("[handoff A] upstream check", {
					previousSection: prev ? prev.className : "none",
					foundScrollVideoSplit: !!svs,
					foundStripVideo: !!video,
				});
			}
			if (!video) {
				return;
			}

			const measure = (ghostEl) => {
				const vh = window.innerHeight;
				// Measure the wrapper rather than the video itself: scroll-video-split adds a
				// flush-alignment translateY to the video, so the video's own rect carries whatever offset happens to be in effect
				// at refresh time (-entry_overhang during the entry lift). The video fills the wrapper, so
				// the wrapper's rect is the untransformed frame box.
				const box = (video.parentElement || video).getBoundingClientRect();
				const docTop = box.top + window.scrollY;
				// The frame rect always goes through ScrollVideoCore.stripFrame — scroll-video-split's
				// measureDocks calls the same function, so the two sides' understanding of "where the frame is" can
				// never diverge again. An inline copy of the object-fit conversion (cover=Math.max
				// plus centering offset) used to live here; after the strip changed to "spread across the column width + column as viewfinder" only that side was updated,
				// the takeoff rect was off by 827px, and the whole handoff animation flew off (2026-07-20).
				const core = window.ScrollVideoCore;
				const frame = core && core.stripFrame ? core.stripFrame(video, box) : null;
				// When parked, the strip carries parkOffset (the translateY that pins the frame's bottom edge to the viewfinder's bottom edge,
				// see the exit segment of measureDocks). The takeoff rect is calibrated to "the frame at the moment of parking", so
				// it must be added — without it the calculation uses the frame at offset=0, the takeoff point lands outside the viewfinder,
				// under the next module, and the ghost image flies entirely outside the viewport (2026-07-20, the root cause of the lost handoff).
				// In the old rendering (frame height == column height) parkOffset is always 0, so this term naturally degrades.
				const parkShift = frame && Number.isFinite(svs.parkOffset) ? svs.parkOffset : 0;
				const strip = frame
					? {
							left: frame.left,
							top: docTop + (frame.top - box.top) + parkShift,
							width: frame.width,
							height: frame.height,
						}
					: { left: box.left, top: docTop, width: box.width, height: box.height };
				const p = this.anchors();
				const sr = this.stickyRect();
				// Landing = Part 1 product's initial large-image state (when the pin starts, inner is exactly at the container top)
				const tgt = { left: sr.left + p.startX, top: sr.docTop + p.startY, width: p.pw * p.startScale };
				// The ghost image must be **the same size and position as the product in the footage**, otherwise the two products are offset during the cross-fade → "ghosting".
				// Preferably derived from the footage constants published by the strip (footageProductHeightFrac, from calib calibration),
				// back-computing the width from the ghost image's own aspect ratio; falls back to the hand-tuned handoff_src_width when unavailable.
				// Measured: with hand-tuned 100% the ghost was 436px tall vs. 378px for the footage product, 15.5% off, so the bottom edge was off by 70px.
				// The aspect ratio must be the **ghost image's own** intrinsic ratio, and read live in the getter (the image may not be decoded yet).
				// Using the landing anchor's p.ph/p.pw is wrong: GSAP sets the ghost's CSS width to tgt.width and then
				// scales it, so rendered height = rendered width × the image's own ratio. When the anchor ratio differs from the image ratio,
				// rendered height = target height × (image ratio / anchor ratio) — after switching to the last-frame image (534×856, ratio 1.603)
				// it measured 6~7% too large, and users saw "the product suddenly got noticeably bigger" (2026-07-26).
				const arOf = () =>
					ghostEl && ghostEl.naturalWidth > 0
						? ghostEl.naturalHeight / ghostEl.naturalWidth
						: p.pw > 0
							? p.ph / p.pw
							: 1;
				// top must be evaluated **lazily**: GSAP uses `y: () => geo.src.top`, read only at the moment of takeoff.
				// This used to hard-code parkShift = svs.parkOffset, which assumed "at the moment of takeoff the strip is exactly parked at
				// parkOffset". scroll-video-split now derives both the frame and the displacement from smoothed progress
				// (2026-07-26 refactor), so the strip consistently lags the raw scroll by 5~11px, while takeoff timing is triggered by
				// parkScrollY (raw scroll) — the assumption no longer holds, the ghost image and the product in the footage
				// differ by those few px, and during the cross-fade **both are visible** (user feedback "ghosting appears"; the same failure mode
				// was already recorded on 2026-07-22). Changed to read the strip's live applied translateY, so alignment is guaranteed by construction.
				const stripTopNoShift = strip.top - parkShift;
				// The whole src is **lazily evaluated**: GSAP uses `() => geo.src.*`, read only at the moment of takeoff.
				// A snapshot hits two pitfalls: ① the strip's size solver needs several passes to converge, so the
				// strip.height measured at refresh may still be an intermediate value (measured 3205 vs. final 3059, shifting the whole ghost image by
				// 126px); ② after the refactor the strip follows smoothed progress, so the translateY at the moment of takeoff doesn't equal
				// parkOffset. Both make the ghost image and the product in the footage misalign → both visible during the cross-fade
				// (user feedback "ghosting"). Reading live keeps them aligned by construction.
				const liveStrip = () => {
					const bx = (video && (video.parentElement || video)).getBoundingClientRect();
					const fr = core && core.stripFrame ? core.stripFrame(video, bx) : null;
					const ty = Number.isFinite(svs.appliedOffset) ? svs.appliedOffset : parkShift;
					return fr
						? { top: fr.top + window.scrollY + ty, left: fr.left, width: fr.width, height: fr.height }
						: { top: bx.top + window.scrollY + ty, left: bx.left, width: bx.width, height: bx.height };
				};
				const srcH = () => {
					const st = liveStrip();
					return Number.isFinite(svs.footageProductHeightFrac) && svs.footageProductHeightFrac > 0
						? svs.footageProductHeightFrac * st.height
						: ((st.width * num(d.handoffSrcWidth, 100)) / 100) * arOf();
				};
				const src = {
					get width() {
						const ar = arOf();
						return ar > 0 ? srcH() / ar : liveStrip().width;
					},
					get height() {
						return srcH();
					},
					get left() {
						const st = liveStrip();
						return st.left + (st.width - this.width) / 2;
					},
					get top() {
						const st = liveStrip();
						if (Number.isFinite(svs.footageProductBottomFrac) && Number.isFinite(svs.footageProductHeightFrac)) {
							// Pin directly to the top edge of the footage product: bottom-edge fraction − height fraction
							return st.top + (svs.footageProductBottomFrac - svs.footageProductHeightFrac) * st.height;
						}
						return st.top + st.height - (st.height * num(d.handoffSrcOffset, 0)) / 100 - srcH();
					},
				};
				const endY = sr.docTop; // Scroll position where the pin starts
				// Takeoff point = the park point published upstream (aligned in both directions; see the comment on
				// takeoffScrollY at the top of the file for why). The src rect is calibrated to "the product in the last frame"
				// (handoff_src_width/offset), so the takeoff moment must be exactly when the footage stops on its last frame and
				// the strip starts fading out.
				const startY = takeoffScrollY({
					leadStart: endY - (vh * num(d.handoffLead, 80)) / 100,
					park: svs.parkScrollY,
					endY,
				});
				return { src, tgt, startY, endY };
			};

			this.A = this.buildFlight({ img: this.product, video, measure, label: "A" });
			if (HO_DEBUG) {
				const g = this.A && this.A.geo;
				console.log("[handoff A] flight path", {
					built: !!this.A,
					takeoffScrollY: g && Math.round(g.startY),
					landingScrollY: g && Math.round(g.endY),
					windowValid: g ? g.endY > g.startY : null,
					parkScrollY: svs.parkScrollY == null ? "strip not measured yet" : Math.round(svs.parkScrollY),
					parkOffset: svs.parkOffset == null ? "strip not measured yet" : Math.round(svs.parkOffset),
					takeoffRect: g && {
						left: Math.round(g.src.left),
						top: Math.round(g.src.top),
						width: Math.round(g.src.width),
					},
					landingRect: g && {
						left: Math.round(g.tgt.left),
						top: Math.round(g.tgt.top),
						width: Math.round(g.tgt.width),
					},
				});
			}
			if (this.A) {
				this.flights.push(this.A);
				// The left video's park point has to wait until it finishes measuring itself (it needs the footage's intrinsic size, which may come after this
				// init). It broadcasts on every re-measure; on receiving it, refresh to re-read the takeoff point, so we don't keep
				// using the geometry from before metadata.
				this.A.svs = svs;
				this.A.onSvsMeasured = () => ScrollTrigger.refresh();
				svs.addEventListener("scroll-video-split:measured", this.A.onSvsMeasured);
			}
		}

		/* ---------- Handoff B: Part 1 parked small image → scatter main image ---------- */

		initB(d) {
			const next = this.section.nextElementSibling;
			const scatter = next && next.querySelector ? next.querySelector("capture-scatter") : null;
			const pad = scatter ? scatter.querySelector(".capture-scatter__product") : null;
			if (!pad || pad.tagName !== "IMG") {
				return;
			}

			const measure = () => {
				const vh = window.innerHeight;
				const r = pad.getBoundingClientRect();
				const tgt = { left: r.left, top: r.top + window.scrollY, width: r.width };
				const p = this.anchors();
				const sr = this.stickyRect();
				const pinEnd = sr.docTop + sr.height - vh; // sticky top scrolls out of the viewport = pin ends
				// Takeoff = where the product parks after the pin ends (inner parks at the bottom of the container)
				const src = {
					left: sr.left + p.endX,
					top: pinEnd + p.endY,
					width: p.pw * p.endScale,
					height: p.ph * p.endScale,
				};
				// Late takeoff: breaks away only once the landing spot rises to near the bottom edge of the viewport; the start is never earlier than the pin end
				const startY = Math.max(tgt.top - (vh * num(d.handoffBStart, 90)) / 100, pinEnd + 8);
				const endY = Math.max(tgt.top + r.height / 2 - (vh * num(d.handoffBEnd, 55)) / 100, startY + 1);
				return { src, tgt, startY, endY };
			};

			this.B = this.buildFlight({ img: pad, measure, label: "B" });
			if (this.B) {
				this.B.pad = pad;
				this.flights.push(this.B);
			}
		}

		/* ---------- Common: ghost flight in document coordinates ---------- */

		buildFlight({ img, video, measure, label }) {
			const ghost = document.createElement("img");
			ghost.alt = "";
			ghost.setAttribute("aria-hidden", "true");
			// The ghost image is attached to body, in the root stacking context, so its z-index must sit between the two sides:
			// above both the takeoff and landing sections (scroll-video-split.css gives the previous section 2,
			// and this section's container 1), and below .header-section's sticky 50 — before the handoff
			// the video is covered by the header, so if the ghost image were above the header (original value 90), the product would, at the moment of takeoff,
			// jump from behind the header to in front of it (2026-07-17). This also stays clear of .side-panel's 90,
			// so the product won't cover the cart drawer when it's open.
			ghost.style.cssText =
				"position:absolute;top:0;left:0;z-index:40;pointer-events:none;visibility:hidden;transform-origin:0 0;";
			document.body.appendChild(ghost);

			const flight = { ghost, video, label, geo: measure(ghost) };
			const remeasure = () => {
				flight.geo = measure(ghost);
				ghost.style.width = flight.geo.tgt.width + "px";
				copySources(ghost, img, flight.geo.tgt.width, video && svsGhostSrc(video));
				// GSAP's fromTo defaults to immediateRender:true — function-based start values like `y: () => geo.src.top`
				// are evaluated and cached **at the moment the timeline is created**, when the strip's size solver hasn't
				// converged yet (measured strip.height 3205 → final 3059). Without invalidate, changing src into a
				// lazy getter doesn't help — GSAP still uses the old value, so the ghost image and the product in the footage are offset by
				// 100+px, and during the cross-fade both products are visible at once (user feedback "ghosting").
				// invalidate() clears the recorded start/end values, so they're re-evaluated on the next render.
				if (flight.tl) {
					flight.tl.invalidate();
					// invalidate() only clears the recorded start/end values; it **does not** re-apply the from state. And handoff A's
					// scale tween only starts at 40% of the flight (HOLD); the first 40% relies entirely on fromTo's
					// immediateRender pre-writing the starting scale — once invalidate clears that, nobody writes it before the tween starts,
					// so scale stays at the previous 1 (= the landing size), hence "noticeably bigger right at takeoff"
					// (2026-07-26 user feedback). Explicitly restore the takeoff scale here.
					if (window.gsap && flight.geo && flight.geo.tgt.width > 0) {
						window.gsap.set(ghost, { scale: flight.geo.src.width / flight.geo.tgt.width });
					}
				}
				if (DEBUG) {
					this.drawDebug(flight);
				}
			};
			// After the ghost image finishes decoding it must be measured again: src.width is back-computed from "target height ÷ the image's own aspect ratio",
			// and at the moment scale was recorded the image often hasn't decoded yet, so naturalWidth=0 → arOf() falls back to the anchor ratio
			// → rendered height = target height × (image ratio / anchor ratio), measured 8% too large (user feedback "suddenly noticeably bigger",
			// 2026-07-26). Re-measure only once, to avoid triggering each other with copySources.
			let reflowedOnLoad = false;
			ghost.addEventListener("load", () => {
				if (reflowedOnLoad) {
					return;
				}
				reflowedOnLoad = true;
				remeasure();
			});
			remeasure();
			// refreshInit fires before ScrollTrigger recomputes start/end: geometry is updated first,
			// then invalidateOnRefresh re-reads the function-based tween values.
			flight.onRefreshInit = remeasure;
			ScrollTrigger.addEventListener("refreshInit", flight.onRefreshInit);

			const tl = gsap.timeline({
				scrollTrigger: {
					start: () => flight.geo.startY,
					end: () => flight.geo.endY,
					scrub: true,
					invalidateOnRefresh: true,
					onUpdate: () => this.applyStates(),
					onToggle: () => this.applyStates(),
				},
			});
			if (label === "A") {
				// Handoff A: first a "straight drop" segment — during the first HOLD portion only y moves, with x and scale locked at the takeoff values,
				// so the product falls vertically at its original size (DROP_FRAC controls the vertical distance this segment covers); then x/scale
				// ease in from zero velocity, merging with the continuing y fall, seamlessly growing into Part 1's opening large image.
				// Continuity of the landing spot is guaranteed by the anchors (hoStart*); both y segments drive the same property, and the second has immediateRender:false
				// so that t=0 only honors the first segment's start, avoiding GSAP's fromTo overwrite.
				const HOLD = 0.4; // Fraction of the whole handoff scroll window taken by the straight drop (0~1)
				const DROP_FRAC = 0.5; // Fraction of the src→tgt vertical distance covered by the straight segment
				const midY = () => flight.geo.src.top + (flight.geo.tgt.top - flight.geo.src.top) * DROP_FRAC;
				tl.fromTo(ghost, { y: () => flight.geo.src.top }, { y: midY, ease: "power1.in", duration: HOLD }, 0)
					.fromTo(
						ghost,
						{ y: midY },
						{ y: () => flight.geo.tgt.top, ease: "power1.out", duration: 1 - HOLD, immediateRender: false },
						HOLD,
					)
					.fromTo(
						ghost,
						{ x: () => flight.geo.src.left },
						{ x: () => flight.geo.tgt.left, ease: "power1.inOut", duration: 1 - HOLD },
						HOLD,
					)
					.fromTo(
						ghost,
						{ scale: () => flight.geo.src.width / flight.geo.tgt.width },
						{ scale: 1, ease: "power1.inOut", duration: 1 - HOLD },
						HOLD,
					);
			} else {
				// Handoff B (Part 1 parked small image → scatter main image): the original diagonal dive.
				// x/scale use inOut, y uses power2.in — the sense of falling comes from this gravity curve.
				tl.fromTo(
					ghost,
					{ x: () => flight.geo.src.left, scale: () => flight.geo.src.width / flight.geo.tgt.width },
					{ x: () => flight.geo.tgt.left, scale: 1, ease: "power1.inOut", duration: 1 },
					0,
				).fromTo(
					ghost,
					{ y: () => flight.geo.src.top },
					{ y: () => flight.geo.tgt.top, ease: "power2.in", duration: 1 },
					0,
				);
			}
			// During the takeoff segment the left video fades out entirely (the last-frame product is burned into the footage; two products would give it away);
			// scrub makes reverse playback fade it back automatically. Geometry mode only: in manifest mode
			// scroll-video-split's own opacity curve fades the strip out at pDone
			// (see its exit segment); both sides writing the same style.opacity would overwrite each other and
			// flicker near the park point — only one writer per property (2026-07-20).
			const svsHost = video && video.closest("scroll-video-split");
			if (video && !(svsHost && svsHost.dataset.dockManifest)) {
				tl.fromTo(video, { opacity: 1 }, { opacity: 0, ease: "none", duration: 0.3 }, 0);
			}
			flight.tl = tl;
			// Tell the strip "the ghost image is in place and will take over". Only then does the strip dare to fade itself out at pDone —
			// otherwise (GSAP not loaded / anchor missing / no Part 1 on this page) nobody takes over after the fade-out,
			// and the product would vanish into thin air (measured 2026-07-26: hard cut + flight not built = the frame is completely empty at +40px).
			if (svsHost2(video)) {
				svsHost2(video).dataset.handoffArmed = "1";
			}
			return flight;
		}

		// Idempotent state machine: the only input is scrollY. Scrolling, scrub updates, refresh, and anchor jumps
		// all converge here; repeated calls have no side effects.
		applyStates() {
			const y = window.scrollY;
			const A = this.A;
			const B = this.B;
			if (A) {
				A.ghost.style.visibility = y > A.geo.startY && y < A.geo.endY ? "visible" : "hidden";
			}
			if (B) {
				B.ghost.style.visibility = y > B.geo.startY && y < B.geo.endY ? "visible" : "hidden";
				// The real scatter image stays hidden until delivery before landing (visibility doesn't affect its layout measurement)
				B.pad.style.visibility = y >= B.geo.endY ? "" : "hidden";
			}
			// The real Part 1 product is only visible when "A has landed (or is disabled) and B hasn't taken off yet (or is disabled)";
			// data-handoff-hold keeps the same state when the snippet rebuilds on resize.
			const shown = (!A || y >= A.geo.endY) && (!B || y <= B.geo.startY);
			if (shown !== this.productShown) {
				this.productShown = shown;
				if (shown) {
					delete this.product.dataset.handoffHold;
				} else {
					this.product.dataset.handoffHold = "1";
				}
				this.product.style.opacity = shown ? "1" : "0";
				if (this.indicatorDot) {
					this.indicatorDot.style.opacity = shown ? "" : "0";
				}
			}
		}

		drawDebug(flight) {
			(flight.debugEls || []).forEach((el) => el.remove());
			const p = this.anchors();
			const mk = (rect, color, text) => {
				const el = document.createElement("div");
				el.textContent = text;
				el.style.cssText =
					"position:absolute;z-index:91;pointer-events:none;outline:2px dashed " +
					color +
					";font:600 12px/1.4 system-ui,sans-serif;color:" +
					color +
					";padding:2px;";
				el.style.left = rect.left + "px";
				el.style.top = rect.top + "px";
				el.style.width = rect.width + "px";
				el.style.height = (rect.height || (rect.width * p.ph) / p.pw) + "px";
				document.body.appendChild(el);
				this.debugEls.push(el);
				return el;
			};
			flight.debugEls = [
				mk(flight.geo.src, "#e11d48", "handoff-" + flight.label + " src"),
				mk(flight.geo.tgt, "#2563eb", "handoff-" + flight.label + " tgt"),
			];
		}

		destroy() {
			if (this.onScroll) {
				window.removeEventListener("scroll", this.onScroll);
			}
			if (this.raf) {
				cancelAnimationFrame(this.raf);
			}
			this.flights.forEach((f) => {
				ScrollTrigger.removeEventListener("refreshInit", f.onRefreshInit);
				if (f.svs && f.onSvsMeasured) {
					f.svs.removeEventListener("scroll-video-split:measured", f.onSvsMeasured);
				}
				if (f.tl) {
					if (f.tl.scrollTrigger) {
						f.tl.scrollTrigger.kill();
					}
					f.tl.kill();
				}
				f.ghost.remove();
				if (f.video) {
					gsap.set(f.video, { clearProps: "opacity" });
				}
				if (f.pad) {
					f.pad.style.visibility = "";
				}
			});
			this.flights = [];
			this.debugEls.forEach((el) => el.remove());
			this.debugEls = [];
			if (this.product && this.product.tagName === "IMG") {
				delete this.product.dataset.handoffHold;
				this.product.style.opacity = "1";
			}
			if (this.indicatorDot) {
				this.indicatorDot.style.opacity = "";
			}
		}
	}

	/* ---------- Lifecycle ---------- */

	let instances = [];

	// Real probe (overwrites the fallback at the top of the file): condenses "was handoff A actually built, where is the window, what state is the ghost
	// image in right now" into one object, rendered on screen by the [v2] floating box.
	window.__handoffPeek = () => {
		const h = instances[0];
		const A = h && h.A;
		return {
			deviceGate: eligible(),
			instanceCount: instances.length,
			A: A
				? {
						startY: Math.round(A.geo.startY),
						endY: Math.round(A.geo.endY),
						scrollY: Math.round(window.scrollY),
						ghostVisible: A.ghost.style.visibility || "(unset)",
						ghostHasImage: !!(A.ghost.currentSrc || A.ghost.src),
						takeoffTop: Math.round(A.geo.src.top),
						landingTop: Math.round(A.geo.tgt.top),
					}
				: "not built",
		};
	};

	const initAll = () => {
		if (instances.length || !eligible()) {
			return;
		}
		document.querySelectorAll("[data-section-handoff]").forEach((el) => {
			const h = new Handoff(el);
			if (h.flights.length) {
				instances.push(h);
			}
		});
		if (instances.length) {
			ScrollTrigger.refresh();
		}
	};

	const destroyAll = () => {
		instances.forEach((h) => h.destroy());
		instances = [];
	};

	const reinit = () => {
		destroyAll();
		initAll();
	};

	// Runs after Part 1 snippet's load init (which registers its listener first during HTML parsing),
	// so by the time execution gets here the anchor dataset is ready.
	if (document.readyState === "complete") {
		initAll();
	} else {
		window.addEventListener("load", initAll);
	}

	mqDesktop.addEventListener("change", reinit);
	mqMotion.addEventListener("change", reinit);

	// 400ms: later than Part 1 snippet's 200ms rebuild, so refresh reads the rebuilt anchors.
	// ScrollTrigger's built-in resize refresh may use stale transitional values; this is a fallback correction.
	let resizeTimer;
	window.addEventListener("resize", () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(() => {
			if (instances.length) {
				ScrollTrigger.refresh();
			}
		}, 400);
	});

	// theme editor adds/removes/reorders sections (these events only fire in the editor)
	let editorTimer;
	["shopify:section:load", "shopify:section:unload", "shopify:section:reorder"].forEach((evt) => {
		document.addEventListener(evt, () => {
			clearTimeout(editorTimer);
			editorTimer = setTimeout(reinit, 300);
		});
	});
})();

/* Unit test entry point (module doesn't exist in the browser, so this is dead code). */
if (typeof module === "object" && module && module.exports) {
	module.exports = { takeoffScrollY };
}
